/**
 * Service Request Pipeline Types
 *
 * The Service Request pipeline tracks customer-initiated remote service changes —
 * things like "change NBN speed", "add a VoIP extension", "reset WiFi password",
 * "update billing email". Lighter than the Fault pipeline: no SLA, no deployment,
 * no severity escalation. All work is remote.
 *
 * Two pipelines exist:
 *
 * 1. Standard pipeline (most request types):
 *    01-Intake → 02-In-Progress → 03-Awaiting-Provider → 04-Awaiting-Customer → 05-Completed
 *    Stages 03 and 04 are skippable.
 *
 * 2. NBN Upgrade pipeline (when requestType === 'nbn_upgrade'):
 *    NBN-01-Intake → NBN-02-Awaiting-NBN → NBN-03-NBN-Appointment-Booked
 *    → NBN-04-NBN-Complete → NBN-05-Awaiting-Our-Scheduling
 *    → NBN-06-Our-Install-Scheduled → NBN-07-Completed
 *    Mimics a /deal flow for coordinating with NBN + our install visit.
 *
 * Exit action from Completed (either pipeline): optional "Send to Billing".
 */

import type { ClientUpdate } from './fault.types';
import type { BillingLineItem, BillingTaxMode } from '@shared/money/billing-lines';

export type StandardServiceRequestStage =
  | '01-Intake'
  | '02-In-Progress'
  | '03-Awaiting-Provider'
  | '04-Awaiting-Customer'
  | '05-Completed';

export type NBNServiceRequestStage =
  | 'NBN-01-Intake'
  | 'NBN-02-Churned'
  | 'NBN-03-Appt-Confirmed'
  | 'NBN-03a-Held-NBN-Civil'
  | 'NBN-03b-Held-Customer'
  | 'NBN-04-Check-Status'
  | 'NBN-05-Completed';

export type ServiceRequestStage =
  | StandardServiceRequestStage
  | NBNServiceRequestStage;

export const SERVICE_REQUEST_STAGE_ORDER: StandardServiceRequestStage[] = [
  '01-Intake',
  '02-In-Progress',
  '03-Awaiting-Provider',
  '04-Awaiting-Customer',
  '05-Completed',
];

export const NBN_SERVICE_REQUEST_STAGE_ORDER: NBNServiceRequestStage[] = [
  'NBN-01-Intake',
  'NBN-02-Churned',
  'NBN-03-Appt-Confirmed',
  'NBN-03a-Held-NBN-Civil',
  'NBN-03b-Held-Customer',
  'NBN-04-Check-Status',
  'NBN-05-Completed',
];

/** All stages (standard + NBN) — useful for iterating all possible values */
export const ALL_SERVICE_REQUEST_STAGES: ServiceRequestStage[] = [
  ...SERVICE_REQUEST_STAGE_ORDER,
  ...NBN_SERVICE_REQUEST_STAGE_ORDER,
];

export const SERVICE_REQUEST_STAGE_LABELS: Record<ServiceRequestStage, string> = {
  '01-Intake': 'Intake',
  '02-In-Progress': 'In Progress',
  '03-Awaiting-Provider': 'Awaiting Provider',
  '04-Awaiting-Customer': 'Awaiting Customer',
  '05-Completed': 'Completed',
  'NBN-01-Intake': 'Intake',
  'NBN-02-Churned': 'Churned',
  'NBN-03-Appt-Confirmed': 'NBN Appt Confirmed',
  'NBN-03a-Held-NBN-Civil': 'Held \u2013 NBN Civil',
  'NBN-03b-Held-Customer': 'Held \u2013 Customer GPO',
  'NBN-04-Check-Status': 'Check Status',
  'NBN-05-Completed': 'Completed',
};

export const SERVICE_REQUEST_STAGE_DESCRIPTIONS: Record<ServiceRequestStage, string> = {
  '01-Intake': 'Request received, not yet actioned',
  '02-In-Progress': 'Office is actively working on it',
  '03-Awaiting-Provider': 'Waiting on NBN / Starlink / VoIP supplier',
  '04-Awaiting-Customer': 'Waiting on customer response or approval',
  '05-Completed': 'Done — with optional billing',
  'NBN-01-Intake': 'NBN upgrade requested, not yet actioned',
  'NBN-02-Churned': 'Customer churned across to us — order placed with NBN',
  'NBN-03-Appt-Confirmed': 'NBN appointment confirmed — date/time locked in',
  'NBN-03a-Held-NBN-Civil': 'Held — awaiting NBN civil / additional work',
  'NBN-03b-Held-Customer': 'Held — awaiting customer (GPO / access / decision)',
  'NBN-04-Check-Status': 'NBN work done — check if online automatically or needs our config',
  'NBN-05-Completed': 'Customer is live on new NBN service',
};

export const SERVICE_REQUEST_STAGE_COLORS: Record<ServiceRequestStage, {
  dot: string;
  bg: string;
  text: string;
  badge: string;
  bar: string;
  border: string;
}> = {
  '01-Intake': {
    dot: 'bg-sky-400',
    bg: 'from-sky-500/20 to-sky-600/5',
    text: 'text-sky-400',
    badge: 'bg-sky-500/20 text-sky-300',
    bar: 'bg-sky-400/60',
    border: 'border-sky-500/20',
  },
  '02-In-Progress': {
    dot: 'bg-blue-400',
    bg: 'from-blue-500/20 to-blue-600/5',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300',
    bar: 'bg-blue-400/60',
    border: 'border-blue-500/20',
  },
  '03-Awaiting-Provider': {
    dot: 'bg-amber-400',
    bg: 'from-amber-500/20 to-amber-600/5',
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300',
    bar: 'bg-amber-400/60',
    border: 'border-amber-500/20',
  },
  '04-Awaiting-Customer': {
    dot: 'bg-purple-400',
    bg: 'from-purple-500/20 to-purple-600/5',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300',
    bar: 'bg-purple-400/60',
    border: 'border-purple-500/20',
  },
  '05-Completed': {
    dot: 'bg-emerald-400',
    bg: 'from-emerald-500/20 to-emerald-600/5',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300',
    bar: 'bg-emerald-400/60',
    border: 'border-emerald-500/20',
  },
  // NBN upgrade stages — each with a distinct colour step so the progression
  // reads visually like a /deal pipeline.
  'NBN-01-Intake': {
    dot: 'bg-sky-400',
    bg: 'from-sky-500/20 to-sky-600/5',
    text: 'text-sky-400',
    badge: 'bg-sky-500/20 text-sky-300',
    bar: 'bg-sky-400/60',
    border: 'border-sky-500/20',
  },
  'NBN-02-Churned': {
    dot: 'bg-orange-400',
    bg: 'from-orange-500/20 to-orange-600/5',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300',
    bar: 'bg-orange-400/60',
    border: 'border-orange-500/20',
  },
  'NBN-03-Appt-Confirmed': {
    dot: 'bg-cyan-400',
    bg: 'from-cyan-500/20 to-cyan-600/5',
    text: 'text-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-300',
    bar: 'bg-cyan-400/60',
    border: 'border-cyan-500/20',
  },
  'NBN-03a-Held-NBN-Civil': {
    dot: 'bg-rose-400',
    bg: 'from-rose-500/20 to-rose-600/5',
    text: 'text-rose-400',
    badge: 'bg-rose-500/20 text-rose-300',
    bar: 'bg-rose-400/60',
    border: 'border-rose-500/20',
  },
  'NBN-03b-Held-Customer': {
    dot: 'bg-amber-400',
    bg: 'from-amber-500/20 to-amber-600/5',
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300',
    bar: 'bg-amber-400/60',
    border: 'border-amber-500/20',
  },
  'NBN-04-Check-Status': {
    dot: 'bg-purple-400',
    bg: 'from-purple-500/20 to-purple-600/5',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300',
    bar: 'bg-purple-400/60',
    border: 'border-purple-500/20',
  },
  'NBN-05-Completed': {
    dot: 'bg-emerald-400',
    bg: 'from-emerald-500/20 to-emerald-600/5',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300',
    bar: 'bg-emerald-400/60',
    border: 'border-emerald-500/20',
  },
};

/**
 * Returns true if a stage value belongs to the NBN-upgrade pipeline.
 */
export function isNBNStage(stage: ServiceRequestStage | string): stage is NBNServiceRequestStage {
  return typeof stage === 'string' && stage.startsWith('NBN-');
}

/**
 * Returns true if this request should use the NBN pipeline based on its type.
 */
export function usesNBNPipeline(requestType: string): boolean {
  return requestType === 'nbn_upgrade';
}

/**
 * Returns the correct stage order (standard or NBN) for a given request type.
 */
export function getPipelineOrder(
  requestType: string,
): ServiceRequestStage[] {
  return usesNBNPipeline(requestType)
    ? [...NBN_SERVICE_REQUEST_STAGE_ORDER]
    : [...SERVICE_REQUEST_STAGE_ORDER];
}

/**
 * Returns the initial stage for a new request based on its type.
 */
export function getInitialStage(requestType: string): ServiceRequestStage {
  return usesNBNPipeline(requestType) ? 'NBN-01-Intake' : '01-Intake';
}

/**
 * Returns the completed stage for a request's pipeline.
 */
export function getCompletedStage(
  requestType: string,
): ServiceRequestStage {
  return usesNBNPipeline(requestType) ? 'NBN-05-Completed' : '05-Completed';
}

/**
 * Returns true if a stage is one of the NBN "held" sub-states.
 * Held stages block progression and typically have an ETA for resolution.
 */
export function isNBNHeldStage(
  stage: ServiceRequestStage | string,
): stage is 'NBN-03a-Held-NBN-Civil' | 'NBN-03b-Held-Customer' {
  return stage === 'NBN-03a-Held-NBN-Civil' || stage === 'NBN-03b-Held-Customer';
}

/**
 * The telco request types — the static fallback set, and the only ones with
 * bespoke behaviour (`usesNBNPipeline`, the relocation detail block).
 *
 * This union stays CLOSED so the label/description maps below keep their
 * exhaustiveness check. What lands on a record is `ServiceRequestTypeKey`.
 */
export type ServiceRequestType =
  | 'nbn_upgrade'
  | 'speed_plan_change'
  | 'voip_changes'
  | 'account_billing_update'
  | 'config_remote_work'
  | 'relocating'
  | 'account_closure'
  | 'other';

/**
 * What is actually stored on a `service_requests` doc: whatever request-type
 * key the tenant's trade pack defines (`gate_not_closing`, `test_and_tag`, …).
 *
 * Open on purpose. The literals above still autocomplete, but a pack key is a
 * plain string and the record has to round-trip it forever — a request logged
 * under one pack must still read back after the tenant's pack changes.
 *
 * Consequence, and the reason this type exists rather than a widened union:
 * `SERVICE_REQUEST_TYPE_LABELS[someStoredKey]` no longer type-checks, because
 * a stored key genuinely might not be in that map. Use `useServiceRequestTypes()`
 * — its `label()` falls back through pack → static telco list → humanised key,
 * so a row never renders `undefined`.
 */
export type ServiceRequestTypeKey = ServiceRequestType | (string & {});

export const SERVICE_REQUEST_TYPE_LABELS: Record<ServiceRequestType, string> = {
  nbn_upgrade: 'NBN Upgrade',
  speed_plan_change: 'Speed / Plan Change',
  voip_changes: 'VoIP Changes',
  account_billing_update: 'Account / Billing Update',
  config_remote_work: 'Configuration / Remote Work',
  relocating: 'Relocating',
  account_closure: 'Account Closure',
  other: 'Other',
};

export const SERVICE_REQUEST_TYPE_DESCRIPTIONS: Record<ServiceRequestType, string> = {
  nbn_upgrade: 'FTTN→FTTP, FTTC→FTTP, HFC→FTTP, speed tier upgrade requiring NBN site work',
  speed_plan_change: 'NBN/Starlink speed change, plan upgrade/downgrade, add data pack',
  voip_changes: 'Add/remove lines, change extensions, update call routing',
  account_billing_update: 'Payment details, contact info, billing cycle changes',
  config_remote_work: 'Router config, WiFi password, port forwarding, password resets',
  relocating: 'Customer moving house — new address, move date, service relocation',
  account_closure: 'Cancel a service — close billing, close with provider, confirm with customer',
  other: 'Anything else',
};

/**
 * Account closure (requestType === 'account_closure') — the three things that
 * have to happen when a customer cancels, each owned by a different team:
 * stop charging them, cancel upstream so we stop being charged, and tell the
 * customer it's done. Theo has no team/department concept, so each part names
 * a person picked per request.
 */
export type ServiceRequestClosurePart = 'billing' | 'provisioning' | 'customer_service';

/** Display order + labels for the closure checklist. */
export const SERVICE_REQUEST_CLOSURE_PARTS: ReadonlyArray<{
  key: ServiceRequestClosurePart;
  label: string;
}> = [
  { key: 'billing', label: 'Billing' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'customer_service', label: 'Customer service' },
];

export interface ServiceRequestClosurePartState {
  assigneeId?: string;
  assigneeName?: string;
  /** ISO timestamp — set when the part is ticked done, removed when unticked */
  doneAt?: string;
  doneById?: string;
  doneByName?: string;
}

export interface ServiceRequestClosure {
  /** Date the service should end — YYYY-MM-DD */
  effectiveDate?: string;
  reason?: string;
  parts: Partial<Record<ServiceRequestClosurePart, ServiceRequestClosurePartState>>;
}

export type ServiceRequestPriority = 'low' | 'medium' | 'high' | 'urgent';

export const SERVICE_REQUEST_PRIORITY_LABELS: Record<ServiceRequestPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const SERVICE_REQUEST_PRIORITY_COLORS: Record<ServiceRequestPriority, {
  dot: string;
  text: string;
  badge: string;
}> = {
  low: {
    dot: 'bg-slate-400',
    text: 'text-slate-400',
    badge: 'bg-slate-500/20 text-slate-300',
  },
  medium: {
    dot: 'bg-blue-400',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300',
  },
  high: {
    dot: 'bg-amber-400',
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300',
  },
  urgent: {
    dot: 'bg-rose-500',
    text: 'text-rose-400',
    badge: 'bg-rose-500/20 text-rose-300',
  },
};

export type ServiceRequestIntakeMethod = 'phone' | 'email' | 'sms' | 'manual';

export const SERVICE_REQUEST_INTAKE_METHOD_LABELS: Record<ServiceRequestIntakeMethod, string> = {
  phone: 'Phone Call',
  email: 'Email',
  sms: 'SMS',
  manual: 'Manual Entry',
};

export type ServiceRequestResolution =
  | 'completed'
  | 'no_action_needed'
  | 'declined_by_customer'
  | 'deferred';

export const SERVICE_REQUEST_RESOLUTION_LABELS: Record<ServiceRequestResolution, string> = {
  completed: 'Completed',
  no_action_needed: 'No Action Needed',
  declined_by_customer: 'Declined by Customer',
  deferred: 'Deferred',
};

export interface ServiceRequestActivity {
  id: string;
  timestamp: string; // ISO string
  stage: ServiceRequestStage | string;
  author: string; // User display name or ID
  action: string; // Short action label (e.g. "Stage advanced", "Internal note added")
  details: string;
  /**
   * Optional marker for special activity kinds that the timeline may want to
   * render differently (e.g. stage overrides get a distinct icon/colour).
   */
  kind?: 'stage_change' | 'stage_override' | 'note' | 'customer_contact' | 'billing' | 'line_items' | 'variation' | 'nbn' | 'job' | 'tag' | 'attachment' | 'other';
}

/**
 * A file uploaded against a service request — invoices, screenshots, NBN
 * confirmation emails, customer-supplied photos, etc. Stored in
 * Firebase Storage; the array on the request doc holds metadata + a
 * download URL. Categorised purely for icon rendering.
 */
export type ServiceRequestAttachmentCategory =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'other';

export interface ServiceRequestAttachment {
  id: string;
  url: string;
  storagePath: string;
  fileName: string;
  fileType: string;        // MIME type
  fileSize: number;        // bytes
  category: ServiceRequestAttachmentCategory;
  caption?: string;
  uploadedAt: string;      // ISO
  uploadedBy: string;      // userId
  uploadedByName: string;  // denormalized
}

/**
 * Line item categories for service request products / services.
 * Used for filtering, icons, and default behaviour (e.g. `service_plan` defaults
 * to recurring billing, `hardware` defaults to one-time).
 */
export type ServiceRequestLineItemCategory =
  | 'service_plan' // NBN/Starlink/VoIP plan — usually recurring
  | 'hardware' // Router, handset, ATA — one-time
  | 'labour' // Remote config, setup time — one-time
  | 'activation_fee' // One-off setup/activation fee
  | 'credit' // Discount or credit (negative total)
  | 'other';

export const SERVICE_REQUEST_LINE_ITEM_CATEGORY_LABELS: Record<ServiceRequestLineItemCategory, string> = {
  service_plan: 'Service Plan',
  hardware: 'Hardware',
  labour: 'Labour',
  activation_fee: 'Activation Fee',
  credit: 'Credit / Discount',
  other: 'Other',
};

export const SERVICE_REQUEST_LINE_ITEM_CATEGORY_COLORS: Record<ServiceRequestLineItemCategory, {
  badge: string;
  text: string;
  icon: string;
}> = {
  service_plan: {
    badge: 'bg-blue-500/20 text-blue-300',
    text: 'text-blue-400',
    icon: 'Wifi',
  },
  hardware: {
    badge: 'bg-purple-500/20 text-purple-300',
    text: 'text-purple-400',
    icon: 'Package',
  },
  labour: {
    badge: 'bg-amber-500/20 text-amber-300',
    text: 'text-amber-400',
    icon: 'Wrench',
  },
  activation_fee: {
    badge: 'bg-emerald-500/20 text-emerald-300',
    text: 'text-emerald-400',
    icon: 'Zap',
  },
  credit: {
    badge: 'bg-rose-500/20 text-rose-300',
    text: 'text-rose-400',
    icon: 'MinusCircle',
  },
  other: {
    badge: 'bg-slate-500/20 text-slate-300',
    text: 'text-slate-400',
    icon: 'CircleDot',
  },
};

/**
 * A single product / service / charge line on a service request.
 * All prices are ex GST. Use `billingType` to decide whether it's a one-off
 * charge (`one-time`) or a recurring monthly amount (`recurring`).
 */
export interface ServiceRequestLineItem {
  id: string;
  category: ServiceRequestLineItemCategory;
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number; // ex GST, AUD. Can be negative for credits.
  billingType: 'one-time' | 'recurring';
  notes?: string;

  // Optional links to catalog / plan entries so we can trace provenance
  partsCatalogId?: string; // → parts_catalog
  servicePlanId?: string; // → service_plans

  // Audit
  addedAt: string; // ISO
  addedBy: string; // display name
}

/**
 * A named bundle of line items representing an alternative configuration
 * the customer is considering (e.g. "Option A: 100/40", "Option B: 250/25").
 * One variation can be "selected" — its items are copied into the parent
 * request's `lineItems` array and used for billing / provisioning.
 */
export interface ServiceRequestVariation {
  id: string;
  name: string;
  description?: string;
  items: ServiceRequestLineItem[];
  /**
   * When true, this is the variation the customer picked — its items
   * are mirrored into `ServiceRequest.lineItems`.
   */
  isActive?: boolean;
  selectedAt?: string; // ISO — when this variation became active
  selectedBy?: string; // display name
  createdAt: string; // ISO
  createdBy: string; // display name
}

/**
 * Snapshot of the customer's CURRENT service at the time the request was
 * raised — used to show "before vs after" on NBN upgrades and plan changes.
 */
export interface ServiceRequestCurrentSnapshot {
  planName?: string;
  speedTier?: string; // e.g. "50/20"
  technology?: string; // e.g. "FTTN", "HFC"
  monthlyPrice?: number; // ex GST
  notes?: string;
  capturedAt: string; // ISO
}

/**
 * Audit record for an explicit stage override (moving to a past stage or
 * otherwise forcing a non-sequential jump). Kept separately from the
 * activity log so we can surface "this request has been overridden" without
 * scanning every activity entry.
 */
export interface ServiceRequestStageOverride {
  id: string;
  timestamp: string; // ISO
  fromStage: ServiceRequestStage;
  toStage: ServiceRequestStage;
  reason: string;
  overriddenBy: string; // display name
  overriddenById?: string;
}

export interface ServiceRequest {
  id?: string;
  tenantId: string;
  /**
   * Human-readable reference ("SR-0042") assigned once at creation — see
   * shared/src/domain/ticket-number.ts. Optional: tickets created before the
   * backfill ran have none. Display/search only; the doc id stays the key.
   */
  ticketNumber?: string;

  // Customer + service linking
  customerId: string;
  customerName: string;
  serviceId?: string; // Optional link to customer_services/{serviceId}
  serviceType?: string; // 'NBN' | 'Starlink' | 'VoIP' | etc.
  servicePlanName?: string;

  // Request details
  requestType: ServiceRequestTypeKey; // a trade pack's request-type key
  title: string; // Short summary
  description: string; // Full detail from intake
  priority: ServiceRequestPriority;

  // Lifecycle
  stage: ServiceRequestStage;
  intakeMethod: ServiceRequestIntakeMethod;
  intakeDate: string; // ISO timestamp
  intakeBy: string; // User ID
  intakeByName: string;

  // Activity timeline (like fault.investigationHistory)
  activityLog: ServiceRequestActivity[];

  // Customer communications log (reuses the fault ClientUpdate type)
  customerUpdates: ClientUpdate[];

  // Assignment
  assignedTo?: string; // Office staff user ID
  assignedToName?: string;

  // Completion
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  completionNotes?: string;
  resolutionType?: ServiceRequestResolution;

  // Billing exit action (mirror fault pattern)
  sentToBilling?: boolean;
  sentToBillingAt?: string;
  sentToBillingBy?: string;
  sentToBillingByName?: string;
  billingAmount?: number; // Ex-GST subtotal. When billingItems exist this is their sum.
  // 'bad_debtor' is a manual office override for invoiced requests not getting paid.
  billingStatus?: 'to_bill' | 'billed' | 'paid' | 'bad_debtor';
  billedAt?: string;
  billedBy?: string;
  billedByName?: string;
  paidAt?: string;
  paidBy?: string;
  paidByName?: string;
  billingNotes?: string;
  // What's being billed — the itemisation captured at "Send to Billing" time.
  // TWIN of Fault.billingItems (same shared BillingLineItem contract, same
  // capture modal). Distinct from `lineItems` above: those are the REQUEST's
  // scope (one-time + recurring, provisioning-facing), these are the sell-side
  // lines this particular invoice is being raised for.
  billingItems?: BillingLineItem[];
  // Tax treatment captured alongside the items. `billingAmount` stays ex-GST
  // in every mode — see the matching block on Fault.
  billingTaxMode?: BillingTaxMode;
  billingTaxRate?: number; // Decimal rate SNAPSHOT at capture (0.1 = 10%)
  billingTaxAmount?: number;
  billingTotalIncTax?: number;
  badDebtorReason?: string;
  badDebtorFlaggedAt?: string;
  badDebtorFlaggedBy?: string;
  badDebtorFlaggedByName?: string;

  // Xero Integration (V107.52.37+) — populated on first successful push.
  // See docs/XERO_INTEGRATION_PLAN.md §4.3 and src/types/xero.types.ts.
  xeroInvoiceId?: string;       // Xero InvoiceID (UUID) — stable forever
  xeroInvoiceNumber?: string;   // e.g. "INV-2026-0042"
  xeroInvoiceUrl?: string;      // Deep link to view in Xero
  xeroInvoiceStatus?: 'DRAFT' | 'SUBMITTED' | 'AUTHORISED' | 'PAID' | 'VOIDED' | 'DELETED';
  xeroLastSyncedAt?: string;    // ISO timestamp of most recent push/status sync
  xeroPushError?: string;       // Last user-friendly error if push failed (cleared on success)

  // Linked jobs — jobs raised from this request (Create Job button)
  linkedJobIds?: string[];

  /**
   * Field briefing — the concise "do this, do that" the attending tech reads
   * first (Aug 2026). TWIN of `Fault.techBriefing` and `deal.techBriefing`:
   * one human-authored instruction captured when the request is raised into a
   * field job, so the tech isn't reconstructing the work from the request's
   * activity log. Written from QuickRaiseJobModal, rendered by the tech job
   * card above the notes.
   */
  techBriefing?: import('./schema').TechBriefing;

  // Cross-entity conversion links (Jul 2026 unified actions — mirror fault pattern)
  sentToFault?: boolean;
  sentToFaultAt?: string;
  sentToFaultBy?: string;
  linkedFaultId?: string; // Fault created from this request (Send to Fault)
  sourceFaultId?: string; // Set when this request was created FROM a fault
  sentToDeal?: boolean;
  sentToDealAt?: string;
  sentToDealBy?: string;
  linkedDealId?: string; // Deal created from this request (Send to Deal)

  /**
   * Files attached to this request (PDFs, images, docs). Stored in
   * Firebase Storage at service-requests/{tenantId}/{requestId}/...
   */
  attachments?: ServiceRequestAttachment[];

  // ------------------------------------------------------------------
  // Products / line items / variations (V107.52.35)
  // ------------------------------------------------------------------
  /**
   * The active set of items being provisioned / billed. When a variation
   * is "selected", its items are mirrored here. Can also be edited directly
   * without variations for simple requests.
   */
  lineItems?: ServiceRequestLineItem[];

  /** Optional named alternatives the customer is weighing up. */
  variations?: ServiceRequestVariation[];

  /** Which variation is currently active (mirrored into `lineItems`). */
  activeVariationId?: string;

  /** Computed totals — kept in sync by the service layer. */
  oneTimeTotal?: number; // ex GST
  monthlyRecurringTotal?: number; // ex GST
  annualContractValue?: number; // one-time + 12 × recurring (ex GST)

  /** Snapshot of the previous service (for before/after comparisons). */
  currentServiceSnapshot?: ServiceRequestCurrentSnapshot;

  /**
   * Audit trail of stage overrides. Each entry records an explicit
   * non-sequential stage change with a reason.
   */
  stageOverrides?: ServiceRequestStageOverride[];

  // NBN Upgrade pipeline data (only set when requestType === 'nbn_upgrade')
  nbnUpgrade?: {
    /** Target connection technology (e.g. FTTP, FTTC) */
    targetTechnology?: string;
    /** Target speed tier (e.g. "1000/50", "250/25") */
    targetSpeedTier?: string;
    /** NBN order reference number */
    nbnOrderRef?: string;
    /** Date NBN appointment is booked for — YYYY-MM-DD */
    nbnAppointmentDate?: string;
    /** Time window — free text (e.g. "8am–12pm") */
    nbnAppointmentWindow?: string;
    /** When NBN completed their work — ISO timestamp */
    nbnCompletedAt?: string;
    /** When held — estimated resolution date (YYYY-MM-DD) */
    heldEta?: string;
    /** Free-text notes captured when the request was put on hold */
    heldNotes?: string;
    /** ISO timestamp when most recent hold was set */
    heldAt?: string;
  };

  // Relocation data (only set when requestType === 'relocating')
  relocating?: {
    /** Where they're moving TO */
    newAddress?: string;
    /** Move-in / cutover date — YYYY-MM-DD */
    moveDate?: string;
    /** Who is moving (account holder / tenant / business contact) */
    who?: string;
    /** What happens at the OLD address (disconnect / new occupant takes over / keep) */
    oldAddressPlan?: string;
  };

  // Account closure data (only set when requestType === 'account_closure')
  closure?: ServiceRequestClosure;

  // Lifecycle flags
  archived: boolean;
  archivedAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Compute the subtotal of a line item (ex GST). Supports negative unit prices
 * for credits.
 */
export function lineItemSubtotal(item: Pick<ServiceRequestLineItem, 'quantity' | 'unitPrice'>): number {
  const qty = Number.isFinite(item.quantity) ? item.quantity : 0;
  const price = Number.isFinite(item.unitPrice) ? item.unitPrice : 0;
  return qty * price;
}

/**
 * Roll up a list of line items into one-time / recurring / ACV totals.
 * ACV = one-time + 12 × recurring (a common "headline value" metric).
 */
export function computeLineItemTotals(items: ServiceRequestLineItem[] = []): {
  oneTimeTotal: number;
  monthlyRecurringTotal: number;
  annualContractValue: number;
} {
  let oneTime = 0;
  let recurring = 0;
  for (const item of items) {
    const sub = lineItemSubtotal(item);
    if (item.billingType === 'recurring') recurring += sub;
    else oneTime += sub;
  }
  return {
    oneTimeTotal: Number(oneTime.toFixed(2)),
    monthlyRecurringTotal: Number(recurring.toFixed(2)),
    annualContractValue: Number((oneTime + recurring * 12).toFixed(2)),
  };
}

/**
 * Service Request Statistics (for dashboard)
 */
export interface ServiceRequestStats {
  totalOpen: number;
  byStage: Record<ServiceRequestStage, number>;
  byPriority: Record<ServiceRequestPriority, number>;
  /** Keyed by the pack's request-type keys, so this is sparse and open —
   *  a key is absent until a request of that type exists. */
  byType: Record<string, number>;
  averageCompletionHours: number;
  completedThisWeek: number;
  awaitingAction: number; // Intake + In Progress
}
